import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { YalolaLogo } from "@/components/YalolaLogo";
import { Wrench, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { hasAnyRole } from "@/lib/roles";

const ADMIN_ROLES = ["super_admin", "manager", "moderator", "associate"];

/**
 * Full-app maintenance barrier. When maintenance mode is on, EVERY route (incl.
 * the logged-out landing page) is replaced by a polite apology screen. Admins
 * keep full access; a discreet "Espace admin" link lets an admin log in.
 */
export const MaintenanceGate = ({ children }) => {
  const { config, user, login, setConfig, t } = useApp();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const isAdmin = hasAnyRole(user, ADMIN_ROLES);
  const maintenanceOn = !!config?.settings?.maintenance_mode;

  const disableMaintenance = async () => {
    try {
      const { data } = await api.patch("/admin/settings", { maintenance_mode: false });
      setConfig?.(data);
      toast.success(t("maint.disabled", "Mode maintenance désactivé."));
    } catch {
      toast.error(t("common.error", "Erreur"));
    }
  };

  // Admins keep full access — but show a clear indicator so they know it's active.
  if (maintenanceOn && isAdmin) {
    return (
      <>
        {children}
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 rounded-full px-3.5 py-2 shadow-lg text-white text-xs font-medium"
          style={{ bottom: "calc(var(--sa-bottom) + 4.75rem)", background: "var(--yalola-primary)" }}
          data-testid="maintenance-admin-banner"
        >
          <Wrench size={14} className="shrink-0" />
          <span>{t("maint.admin_active", "Maintenance ACTIVE — les visiteurs sont bloqués, vous gardez l'accès.")}</span>
          {user?.role === "super_admin" && (
            <button
              onClick={disableMaintenance}
              className="ml-1 rounded-full bg-white/25 hover:bg-white/40 transition-colors px-2.5 py-1 font-semibold"
              data-testid="maintenance-disable-btn"
            >
              {t("maint.disable", "Désactiver")}
            </button>
          )}
        </div>
      </>
    );
  }

  const maintenance = maintenanceOn && !isAdmin;
  if (!maintenance) return children;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      if (hasAnyRole(u, ADMIN_ROLES)) {
        toast.success(t("maint.welcome_admin", "Bienvenue ! Accès administrateur autorisé."));
        setOpen(false);
      } else {
        toast.error(t("maint.no_access", "Ce compte n'a pas accès pendant la maintenance."));
      }
    } catch (err) {
      toast.error(err?.friendlyMessage || t("maint.bad_credentials", "Identifiants invalides."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4" style={{ background: "var(--yalola-bg)" }}>
      <div className="max-w-md w-full text-center yalola-card p-10" data-testid="maintenance-screen">
        <div className="flex justify-center mb-4"><YalolaLogo size={48} /></div>
        <Wrench size={36} className="mx-auto mb-3" style={{ color: "var(--yalola-primary)" }} />
        <h1 className="text-2xl font-bold font-display">{t("maint.title", "Maintenance en cours")}</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          {config?.settings?.maintenance_message || t("maint.body", "Yalola revient très vite. Merci de votre patience 🙏")}
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-8 inline-flex items-center gap-1.5 text-xs font-medium opacity-60 hover:opacity-100 transition-opacity"
          style={{ color: "var(--yalola-text-muted)" }}
          data-testid="maintenance-admin-link"
        >
          <ShieldCheck size={13} /> Espace admin
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-sm" data-testid="maintenance-admin-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("maint.admin_login", "Connexion administrateur")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3 pt-1">
            <div>
              <Label htmlFor="m-email">{t("maint.email", "Email")}</Label>
              <Input id="m-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus data-testid="maintenance-admin-email" />
            </div>
            <div>
              <Label htmlFor="m-pass">{t("maint.password", "Mot de passe")}</Label>
              <PasswordInput id="m-pass" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="maintenance-admin-password" />
            </div>
            <Button type="submit" disabled={busy} className="pill-primary rounded-full w-full h-11" data-testid="maintenance-admin-submit">
              {busy ? t("maint.connecting", "Connexion…") : t("maint.sign_in", "Se connecter")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
